import {
	isNonRussiaPersonalInfoForm,
	isRussiaRegistrationOksm,
	normalizeRegistrationOksmCode,
} from './countries';
import {
	extractDateDigits,
	formatDateDigitsForApi,
	formatDateDisplay,
	isBirthDateValid,
	isIssuedAtDateValid,
	parseApiDateToDigits,
	parseDateDigits,
} from './passport';

const KZ_OKSM = '398';
const KG_OKSM = '417';
const BY_OKSM = '112';
const UA_OKSM = '804';

/** В справочнике гражданств `51` и `051` — разные записи; оба дают армянскую форму паспорта. */
const ARMENIA_OKSM_CODES = new Set(['51', '051']);

export const isArmeniaCitizenship = (oksmCode?: string | null): boolean => {
	return ARMENIA_OKSM_CODES.has(String(oksmCode ?? '').trim());
};

const PERSONAL_NUMBER_MAX_LENGTH = 14;
const RECORD_NUMBER_LENGTH = 13;
const UA_PASSPORT_LETTERS_LENGTH = 2;
const UA_PASSPORT_DIGITS_LENGTH = 6;
const KG_PASSPORT_LETTERS_LENGTH = 2;
const KG_PASSPORT_DIGITS_LENGTH = 7;

/** Тип документа (актуален для Казахстана: паспорт или удостоверение личности с ИИН). */
export type ForeignDocumentType = 'passport' | 'id_card';

export type ForeignDocumentDetailsStepValues = {
	documentType: ForeignDocumentType;
	passportNumber: string;
	personalNumber: string;
	recordNumber: string;
	secondNameLatin: string;
	latinName: string;
	birthDate: string;
	birthPlace: string;
	issuedAt: string;
	validTill: string;
	issuer: string;
	registrationAddress: string;
};

export const EMPTY_FOREIGN_DOCUMENT_DETAILS: ForeignDocumentDetailsStepValues = {
	documentType: 'passport',
	passportNumber: '',
	personalNumber: '',
	recordNumber: '',
	secondNameLatin: '',
	latinName: '',
	birthDate: '',
	birthPlace: '',
	issuedAt: '',
	validTill: '',
	issuer: '',
	registrationAddress: '',
};

/**
 * Пострановая форма «Введите данные документов» — для всех, кроме РФ
 * (РФ остаётся на экране паспортных данных).
 */
export const usesForeignDocumentDetailsForm = (
	citizenshipOksmCode: string | null,
	_residenceOksmCode?: string | null,
): boolean => {
	const citizenship = normalizeRegistrationOksmCode(citizenshipOksmCode);

	return Boolean(citizenship) && !isRussiaRegistrationOksm(citizenship);
};

// ---------------------------------------------------------------------------
// Форматтеры / экстракторы по типам полей
// ---------------------------------------------------------------------------

export const normalizePassportNumber = (value: string): string => {
	return value.replace(/\s+/g, '').toUpperCase();
};

export const extractPersonalNumberDigits = (
	value: string,
	maxLength: number = PERSONAL_NUMBER_MAX_LENGTH,
): string => {
	return value.replace(/\D/g, '').slice(0, maxLength);
};

export const formatPersonalNumberDisplay = (digits: string): string => {
	const normalized = digits.replace(/\D/g, '');

	if (!normalized) {
		return '';
	}

	return normalized.match(/.{1,4}/g)?.join(' ') ?? normalized;
};

export const extractRecordNumberDigits = (value: string): string => {
	return value.replace(/\D/g, '').slice(0, RECORD_NUMBER_LENGTH);
};

export const formatRecordNumberDisplay = (value: string): string => {
	const digits = extractRecordNumberDigits(value);

	if (!digits) {
		return '';
	}

	const left = digits.slice(0, 8);
	const right = digits.slice(8);

	return right ? `${left}-${right}` : left;
};

export const extractUaPassportValue = (value: string): {letters: string; digits: string} => {
	const cleaned = value.replace(/\s+/g, '').toUpperCase();
	let letters = '';
	let digits = '';

	// Сначала только буквы (2), цифры в эту зону не принимаем; затем только цифры (6).
	for (const char of cleaned) {
		if (letters.length < UA_PASSPORT_LETTERS_LENGTH) {
			if (/[A-ZА-ЯЁ]/.test(char)) {
				letters += char;
			}

			continue;
		}

		if (digits.length < UA_PASSPORT_DIGITS_LENGTH && /\d/.test(char)) {
			digits += char;
		}
	}

	return {letters, digits};
};

/** В стейте храним без пробела (`AA000000`), в поле показываем `AA 000000`. */
export const normalizeUaPassport = (value: string): string => {
	const {letters, digits} = extractUaPassportValue(value);

	return `${letters}${digits}`;
};

export const formatUaPassportDisplay = (value: string): string => {
	const {letters, digits} = extractUaPassportValue(value);

	if (!letters && !digits) {
		return '';
	}

	return digits ? `${letters} ${digits}` : letters;
};

const isUaPassportComplete = (value: string): boolean => {
	const {letters, digits} = extractUaPassportValue(value);

	return letters.length === UA_PASSPORT_LETTERS_LENGTH && digits.length === UA_PASSPORT_DIGITS_LENGTH;
};

const extractLetterDigitPassportValue = (
	value: string,
	lettersLength: number,
	digitsLength: number,
): {letters: string; digits: string} => {
	const cleaned = value.replace(/\s+/g, '').toUpperCase();
	let letters = '';
	let digits = '';

	for (const char of cleaned) {
		if (letters.length < lettersLength) {
			if (/[A-ZА-ЯЁ]/.test(char)) {
				letters += char;
			}

			continue;
		}

		if (digits.length < digitsLength && /\d/.test(char)) {
			digits += char;
		}
	}

	return {letters, digits};
};

export const extractKgPassportValue = (value: string): {letters: string; digits: string} => {
	return extractLetterDigitPassportValue(value, KG_PASSPORT_LETTERS_LENGTH, KG_PASSPORT_DIGITS_LENGTH);
};

/** В стейте храним без пробела (`AB1234567`), в поле показываем `AB 1234567`. */
export const normalizeKgPassport = (value: string): string => {
	const {letters, digits} = extractKgPassportValue(value);

	return `${letters}${digits}`;
};

export const formatKgPassportDisplay = (value: string): string => {
	const {letters, digits} = extractKgPassportValue(value);

	if (!letters && !digits) {
		return '';
	}

	return digits ? `${letters} ${digits}` : letters;
};

const isKgPassportComplete = (value: string): boolean => {
	const {letters, digits} = extractKgPassportValue(value);

	return letters.length === KG_PASSPORT_LETTERS_LENGTH && digits.length === KG_PASSPORT_DIGITS_LENGTH;
};

/** Беларусь: те же 2 буквы + 7 цифр, без пробела в отображении (`AA0000000`). */
export const normalizeByPassport = (value: string): string => normalizeKgPassport(value);

export const formatByPassportDisplay = (value: string): string => {
	const {letters, digits} = extractKgPassportValue(value);

	return `${letters}${digits}`;
};

export const isByPassportComplete = (value: string): boolean => isKgPassportComplete(value);

const KZ_PASSPORT_PREFIX = 'N';
const KZ_PASSPORT_DIGITS_LENGTH = 8;

/** Казахстан: префикс `N` зафиксирован, пользователь вводит 8 цифр. */
export const extractKzPassportDigits = (value: string): string => {
	const raw = value.toUpperCase().replace(/\s+/g, '');
	const withoutPrefix = raw.startsWith(KZ_PASSPORT_PREFIX) ? raw.slice(1) : raw.replace(/^N+/g, '');

	return withoutPrefix.replace(/\D/g, '').slice(0, KZ_PASSPORT_DIGITS_LENGTH);
};

export const normalizeKzPassport = (value: string): string => {
	return `${KZ_PASSPORT_PREFIX}${extractKzPassportDigits(value)}`;
};

export const formatKzPassportDisplay = (value: string): string => {
	return normalizeKzPassport(value || KZ_PASSPORT_PREFIX);
};

const isKzPassportComplete = (value: string): boolean => {
	return extractKzPassportDigits(value).length === KZ_PASSPORT_DIGITS_LENGTH;
};

export const KZ_PASSPORT_DIGITS_PLACEHOLDER = '0'.repeat(KZ_PASSPORT_DIGITS_LENGTH);

const BY_IDENTIFICATION_NUMBER_LENGTH = 14;

/** Беларусь: идентификационный номер — буквы и цифры, 14 символов. */
export const normalizeByIdentificationNumber = (value: string): string => {
	return value
		.replace(/[^0-9a-zA-Zа-яА-ЯёЁ]/g, '')
		.toUpperCase()
		.slice(0, BY_IDENTIFICATION_NUMBER_LENGTH);
};

export const formatByIdentificationNumberDisplay = (value: string): string => {
	return normalizeByIdentificationNumber(value);
};

const isByIdentificationNumberComplete = (value: string): boolean => {
	return normalizeByIdentificationNumber(value).length === BY_IDENTIFICATION_NUMBER_LENGTH;
};

// ---------------------------------------------------------------------------
// Конфиг формы по стране
// ---------------------------------------------------------------------------

export type ForeignDocumentFieldKey =
	| 'passportNumber'
	| 'personalNumber'
	| 'recordNumber'
	| 'secondNameLatin'
	| 'latinName'
	| 'birthDate'
	| 'birthPlace'
	| 'issuedAt'
	| 'validTill'
	| 'issuer'
	| 'registrationAddress';

export type ForeignDocumentFieldInput =
	| 'passportPlain'
	| 'passportAlphanumeric'
	| 'passportUa'
	| 'passportKg'
	| 'passportBy'
	| 'passportKz'
	| 'personalNumber'
	| 'identificationAlphanumeric'
	| 'recordNumber'
	| 'date'
	| 'text'
	| 'textMultiline';

export type ForeignDocumentFieldConfig = {
	key: ForeignDocumentFieldKey;
	label: string;
	placeholder: string;
	input: ForeignDocumentFieldInput;
	/** Для personalNumber (ИИН — 12, персональный/идентификационный — 14). */
	digitsLength?: number;
	/** Поле необязательное (например, «Срок действия (если есть)»). */
	optional?: boolean;
};

export type ForeignDocumentTypeOption = {
	value: ForeignDocumentType;
	label: string;
};

export type ForeignDocumentFormConfig = {
	title: string;
	documentTypeOptions?: ForeignDocumentTypeOption[];
	fields: ForeignDocumentFieldConfig[];
};

const FORM_TITLE = 'Введите данные документов';

const passportPlainField: ForeignDocumentFieldConfig = {
	key: 'passportNumber',
	label: 'Номер паспорта',
	placeholder: '000000000',
	input: 'passportPlain',
};

/** «Другое»: буквы и цифры, без цифровой клавиатуры. */
const documentNumberField: ForeignDocumentFieldConfig = {
	key: 'passportNumber',
	label: 'Номер документа',
	placeholder: 'AA0000000',
	input: 'passportAlphanumeric',
};

const validTillOptionalField: ForeignDocumentFieldConfig = {
	key: 'validTill',
	label: 'Срок действия (если есть)',
	placeholder: '01.01.2030',
	input: 'date',
	optional: true,
};

const passportUaField: ForeignDocumentFieldConfig = {
	key: 'passportNumber',
	label: 'Номер паспорта',
	placeholder: 'AA 000000',
	input: 'passportUa',
};

const passportKgField: ForeignDocumentFieldConfig = {
	key: 'passportNumber',
	label: 'Номер паспорта',
	placeholder: 'AA 0000000',
	input: 'passportKg',
};

const passportByField: ForeignDocumentFieldConfig = {
	key: 'passportNumber',
	label: 'Номер паспорта',
	placeholder: 'AA0000000',
	input: 'passportBy',
};

/** Армения: 2 буквы + 7 цифр, плейсхолдер `AA0000000` (без пробела). */
const passportAmField: ForeignDocumentFieldConfig = {
	key: 'passportNumber',
	label: 'Номер документа',
	placeholder: 'AA0000000',
	input: 'passportBy',
};

const passportKzField: ForeignDocumentFieldConfig = {
	key: 'passportNumber',
	label: 'Номер паспорта',
	placeholder: 'N00000000',
	input: 'passportKz',
};

const personalNumberField = (label: string): ForeignDocumentFieldConfig => ({
	key: 'personalNumber',
	label,
	placeholder: '0000 0000 0000 00',
	input: 'personalNumber',
	digitsLength: 14,
});

const byIdentificationNumberField: ForeignDocumentFieldConfig = {
	key: 'personalNumber',
	label: 'Идентификационный номер',
	placeholder: '0000000A000AA0',
	input: 'identificationAlphanumeric',
	digitsLength: 14,
};

const iinField: ForeignDocumentFieldConfig = {
	key: 'personalNumber',
	label: 'Идентификационный номер (ИИН)',
	placeholder: '0000 0000 0000',
	input: 'personalNumber',
	digitsLength: 12,
};

const recordNumberField: ForeignDocumentFieldConfig = {
	key: 'recordNumber',
	label: 'Номер записи',
	placeholder: '00000000-00000',
	input: 'recordNumber',
	digitsLength: RECORD_NUMBER_LENGTH,
};

const secondNameField: ForeignDocumentFieldConfig = {
	key: 'secondNameLatin',
	label: 'Фамилия (на латинице)',
	placeholder: 'Kim',
	input: 'text',
};

const latinNameField: ForeignDocumentFieldConfig = {
	key: 'latinName',
	label: 'Имя (на латинице)',
	placeholder: 'Zhangali',
	input: 'text',
};

const birthDateField: ForeignDocumentFieldConfig = {
	key: 'birthDate',
	label: 'Дата рождения',
	placeholder: '01.01.2000',
	input: 'date',
};

const birthPlaceField = (placeholder: string): ForeignDocumentFieldConfig => ({
	key: 'birthPlace',
	label: 'Место рождения',
	placeholder,
	input: 'textMultiline',
});

const issuedAtField: ForeignDocumentFieldConfig = {
	key: 'issuedAt',
	label: 'Дата выдачи',
	placeholder: '01.01.2020',
	input: 'date',
};

const validTillField: ForeignDocumentFieldConfig = {
	key: 'validTill',
	label: 'Срок действия',
	placeholder: '01.01.2030',
	input: 'date',
};

const issuerField = (
	label = 'Кем выдан',
	placeholder = 'Орган выдачи',
): ForeignDocumentFieldConfig => ({
	key: 'issuer',
	label,
	placeholder,
	input: 'textMultiline',
});

const registrationAddressRfField: ForeignDocumentFieldConfig = {
	key: 'registrationAddress',
	label: 'Адрес регистрации в РФ',
	placeholder: 'До номера квартиры',
	input: 'textMultiline',
};

const KZ_DOCUMENT_TYPE_OPTIONS: ForeignDocumentTypeOption[] = [
	{value: 'passport', label: 'Паспорт гражданина республики Казахстан'},
	{value: 'id_card', label: 'Удостоверение личности гражданина Казахстана'},
];

export const KAZAKHSTAN_DOCUMENT_TYPE_OPTIONS = KZ_DOCUMENT_TYPE_OPTIONS;

export const isKazakhstanCitizenship = (oksmCode?: string | null): boolean => {
	return String(oksmCode ?? '').trim() === KZ_OKSM;
};

/** Тип документа для API / слотов фото: удостоверение → `kazakh_id_card`, иначе паспорт `kazakh`. */
export const resolveKazakhIdentificationDocumentType = (
	documentType: ForeignDocumentType | null | undefined,
): 'kazakh' | 'kazakh_id_card' => {
	return documentType === 'id_card' ? 'kazakh_id_card' : 'kazakh';
};

const KYRGYZSTAN_CONFIG: ForeignDocumentFormConfig = {
	title: FORM_TITLE,
	fields: [
		passportKgField,
		personalNumberField('Персональный номер'),
		secondNameField,
		latinNameField,
		birthDateField,
		birthPlaceField('Kyrgyz Republic'),
		issuedAtField,
		validTillField,
		issuerField(),
	],
};

const ARMENIA_CONFIG: ForeignDocumentFormConfig = {
	title: FORM_TITLE,
	fields: [
		passportAmField,
		secondNameField,
		latinNameField,
		birthDateField,
		birthPlaceField('Republic of Armenia'),
		issuedAtField,
		validTillOptionalField,
		issuerField(),
	],
};

const BELARUS_CONFIG: ForeignDocumentFormConfig = {
	title: FORM_TITLE,
	fields: [
		passportByField,
		byIdentificationNumberField,
		secondNameField,
		latinNameField,
		birthDateField,
		birthPlaceField('Republic of Belarus'),
		issuedAtField,
		validTillField,
		issuerField(),
	],
};

const KAZAKHSTAN_PASSPORT_CONFIG: ForeignDocumentFormConfig = {
	title: FORM_TITLE,
	documentTypeOptions: KZ_DOCUMENT_TYPE_OPTIONS,
	fields: [
		passportKzField,
		secondNameField,
		latinNameField,
		birthDateField,
		birthPlaceField('Almaty'),
		issuedAtField,
		validTillField,
		issuerField(),
	],
};

const KAZAKHSTAN_ID_CONFIG: ForeignDocumentFormConfig = {
	title: FORM_TITLE,
	documentTypeOptions: KZ_DOCUMENT_TYPE_OPTIONS,
	fields: [
		iinField,
		secondNameField,
		latinNameField,
		birthDateField,
		birthPlaceField('Almaty'),
		issuedAtField,
		validTillField,
		issuerField(),
	],
};

const UKRAINE_CONFIG: ForeignDocumentFormConfig = {
	title: FORM_TITLE,
	fields: [
		passportUaField,
		recordNumberField,
		secondNameField,
		latinNameField,
		birthDateField,
		birthPlaceField('Kyiv'),
		issuedAtField,
		validTillField,
		issuerField('Орган выдачи', 'Орган выдачи'),
	],
};

/** Остальные гражданства (не РФ и не из списка выше). */
const OTHER_CONFIG: ForeignDocumentFormConfig = {
	title: FORM_TITLE,
	fields: [
		documentNumberField,
		secondNameField,
		latinNameField,
		birthDateField,
		birthPlaceField('Berlin'),
		issuedAtField,
		validTillOptionalField,
		issuerField(),
	],
};

const DEFAULT_CONFIG = OTHER_CONFIG;

/** «Адрес регистрации в РФ» — только гражданство не РФ и резидентство РФ. */
const withRegistrationAddressInRf = (
	config: ForeignDocumentFormConfig,
	citizenshipOksmCode: string | null | undefined,
	residenceOksmCode: string | null | undefined,
): ForeignDocumentFormConfig => {
	if (!isNonRussiaPersonalInfoForm(citizenshipOksmCode) || !isRussiaRegistrationOksm(residenceOksmCode)) {
		return config;
	}

	return {
		...config,
		fields: [...config.fields, registrationAddressRfField],
	};
};

export const getForeignDocumentFormConfig = (
	citizenshipOksmCode: string | null | undefined,
	documentType: ForeignDocumentType = 'passport',
	residenceOksmCode?: string | null,
): ForeignDocumentFormConfig => {
	const code = String(citizenshipOksmCode ?? '').trim();

	let config: ForeignDocumentFormConfig;

	if (isArmeniaCitizenship(code)) {
		config = ARMENIA_CONFIG;
	} else {
		switch (code) {
			case KG_OKSM:
				config = KYRGYZSTAN_CONFIG;
				break;
			case BY_OKSM:
				config = BELARUS_CONFIG;
				break;
			case KZ_OKSM:
				config = documentType === 'id_card' ? KAZAKHSTAN_ID_CONFIG : KAZAKHSTAN_PASSPORT_CONFIG;
				break;
			case UA_OKSM:
				config = UKRAINE_CONFIG;
				break;
			default:
				config = DEFAULT_CONFIG;
				break;
		}
	}

	return withRegistrationAddressInRf(config, citizenshipOksmCode, residenceOksmCode);
};

// ---------------------------------------------------------------------------
// Значения / валидация / запрос — по конфигу
// ---------------------------------------------------------------------------

/** Отформатированное значение поля для превью на экране «Давайте всё сверим». */
export const formatForeignDocumentFieldValue = (
	field: ForeignDocumentFieldConfig,
	values: ForeignDocumentDetailsStepValues,
): string => {
	const raw = values[field.key];

	switch (field.input) {
		case 'passportUa':
			return formatUaPassportDisplay(raw);
		case 'passportKg':
			return formatKgPassportDisplay(raw);
		case 'passportBy':
			return formatByPassportDisplay(raw);
		case 'passportKz':
			return formatKzPassportDisplay(raw);
		case 'personalNumber':
			return formatPersonalNumberDisplay(raw);
		case 'identificationAlphanumeric':
			return formatByIdentificationNumberDisplay(raw);
		case 'recordNumber':
			return formatRecordNumberDisplay(raw);
		case 'date':
			return formatDateDisplay(raw);
		default:
			return raw.trim();
	}
};

const isForeignDocumentFieldValid = (
	field: ForeignDocumentFieldConfig,
	values: ForeignDocumentDetailsStepValues,
): boolean => {
	const raw = values[field.key];

	// Необязательное поле: пустое — валидно; заполненное — проверяем по общим правилам.
	if (field.optional && raw.trim().length === 0) {
		return true;
	}

	switch (field.input) {
		case 'passportPlain':
		case 'passportAlphanumeric':
			return normalizePassportNumber(raw).length > 0;
		case 'passportUa':
			return isUaPassportComplete(raw);
		case 'passportKg':
			return isKgPassportComplete(raw);
		case 'passportBy':
			return isByPassportComplete(raw);
		case 'passportKz':
			return isKzPassportComplete(raw);
		case 'personalNumber':
			return extractPersonalNumberDigits(raw, field.digitsLength).length === (field.digitsLength ?? PERSONAL_NUMBER_MAX_LENGTH);
		case 'identificationAlphanumeric':
			return isByIdentificationNumberComplete(raw);
		case 'recordNumber':
			return extractRecordNumberDigits(raw).length === RECORD_NUMBER_LENGTH;
		case 'date':
			if (field.key === 'birthDate') {
				return isBirthDateValid(raw);
			}

			if (field.key === 'issuedAt') {
				return isIssuedAtDateValid(raw);
			}

			return Boolean(parseDateDigits(raw));
		default:
			return raw.trim().length > 0;
	}
};

export const isForeignDocumentDetailsStepValid = (
	values: ForeignDocumentDetailsStepValues,
	citizenshipOksmCode?: string | null,
	residenceOksmCode?: string | null,
): boolean => {
	const config = getForeignDocumentFormConfig(
		citizenshipOksmCode,
		values.documentType,
		residenceOksmCode,
	);

	return config.fields.every((field) => isForeignDocumentFieldValid(field, values));
};

/** Превью полей документа для экрана сверки (включая «Тип документа» для КЗ). */
export const getForeignDocumentReviewFields = (
	citizenshipOksmCode: string | null | undefined,
	values: ForeignDocumentDetailsStepValues,
	residenceOksmCode?: string | null,
): {label: string; value: string}[] => {
	const config = getForeignDocumentFormConfig(
		citizenshipOksmCode,
		values.documentType,
		residenceOksmCode,
	);
	const fields: {label: string; value: string}[] = [];

	if (config.documentTypeOptions) {
		const option =
			config.documentTypeOptions.find((item) => item.value === values.documentType) ??
			config.documentTypeOptions[0];

		fields.push({label: 'Тип документа', value: option.label});
	}

	for (const field of config.fields) {
		fields.push({label: field.label, value: formatForeignDocumentFieldValue(field, values)});
	}

	return fields;
};

export type ForeignIdentificationDocumentApiType =
	| 'armenian'
	| 'belarusian'
	| 'kazakh'
	| 'kazakh_id_card'
	| 'kyrgyz'
	| 'other'
	| 'ukraine';

export const resolveForeignIdentificationDocumentType = (
	citizenshipOksmCode: string | null,
	documentType: ForeignDocumentType,
): ForeignIdentificationDocumentApiType => {
	const code = String(citizenshipOksmCode ?? '').trim();

	if (code === KG_OKSM) {
		return 'kyrgyz';
	}

	if (code === BY_OKSM) {
		return 'belarusian';
	}

	if (code === KZ_OKSM) {
		return resolveKazakhIdentificationDocumentType(documentType);
	}

	if (code === UA_OKSM) {
		return 'ukraine';
	}

	return 'other';
};

export const buildForeignDocumentRequest = (
	citizenshipOksmCode: string | null,
	values: ForeignDocumentDetailsStepValues,
	residenceOksmCode?: string | null,
) => {
	if (!isForeignDocumentDetailsStepValid(values, citizenshipOksmCode, residenceOksmCode)) {
		return null;
	}

	const config = getForeignDocumentFormConfig(
		citizenshipOksmCode,
		values.documentType,
		residenceOksmCode,
	);
	const visibleKeys = new Set(config.fields.map((field) => field.key));

	const identificationDocument: Record<string, string> = {
		type: resolveForeignIdentificationDocumentType(citizenshipOksmCode, values.documentType),
		secondNameLatin: values.secondNameLatin.trim(),
		latinName: values.latinName.trim(),
		birthDate: formatDateDigitsForApi(values.birthDate),
		birthPlace: values.birthPlace.trim(),
		issuedAt: formatDateDigitsForApi(values.issuedAt),
		validTill: formatDateDigitsForApi(values.validTill),
	};

	if (visibleKeys.has('passportNumber')) {
		identificationDocument.number = normalizePassportNumber(values.passportNumber);
	}

	if (visibleKeys.has('personalNumber')) {
		const personalNumberFieldConfig = config.fields.find((field) => field.key === 'personalNumber');

		identificationDocument.personalNumber =
			personalNumberFieldConfig?.input === 'identificationAlphanumeric'
				? normalizeByIdentificationNumber(values.personalNumber)
				: extractPersonalNumberDigits(values.personalNumber, personalNumberFieldConfig?.digitsLength);
	}

	if (visibleKeys.has('recordNumber')) {
		identificationDocument.recordNumber = extractRecordNumberDigits(values.recordNumber);
	}

	if (visibleKeys.has('issuer')) {
		identificationDocument.issuer = values.issuer.trim();
	}

	if (visibleKeys.has('registrationAddress')) {
		identificationDocument.registrationAddress = values.registrationAddress.trim();
	}

	return {identificationDocument};
};

type ApiForeignIdentificationDocument = {
	type?: string | null;
	number?: string | null;
	personalNumber?: string | null;
	identificationNumber?: string | null;
	recordNumber?: string | null;
	secondNameLatin?: string | null;
	latinName?: string | null;
	birthDate?: string | null;
	birthPlace?: string | null;
	issuedAt?: string | null;
	validTill?: string | null;
	issuer?: string | null;
	registrationAddress?: string | null;
};

const readApiText = (value: string | null | undefined): string => value?.trim() ?? '';

/** GET /identification/info → значения шага «Введите данные документов» (не РФ). */
export const mapIdentificationDocumentToForeignDetails = (
	identificationDocument: unknown,
): Partial<ForeignDocumentDetailsStepValues> => {
	if (!identificationDocument || typeof identificationDocument !== 'object') {
		return {};
	}

	const document = identificationDocument as ApiForeignIdentificationDocument;
	const type = document.type;

	if (!type || type === 'unknown' || type === 'russian' || type === 'russian_international') {
		return {};
	}

	const result: Partial<ForeignDocumentDetailsStepValues> = {
		documentType: type === 'kazakh_id_card' ? 'id_card' : 'passport',
	};

	const passportNumber = readApiText(document.number);

	if (passportNumber) {
		result.passportNumber = passportNumber;
	}

	const personalNumber = readApiText(document.personalNumber ?? document.identificationNumber);

	if (personalNumber) {
		result.personalNumber = personalNumber;
	}

	const recordNumber = readApiText(document.recordNumber);

	if (recordNumber) {
		result.recordNumber = recordNumber;
	}

	const secondNameLatin = readApiText(document.secondNameLatin);
	const latinName = readApiText(document.latinName);
	const birthPlace = readApiText(document.birthPlace);
	const issuer = readApiText(document.issuer);
	const registrationAddress = readApiText(document.registrationAddress);
	const birthDate = parseApiDateToDigits(document.birthDate);
	const issuedAt = parseApiDateToDigits(document.issuedAt);
	const validTill = parseApiDateToDigits(document.validTill);

	if (secondNameLatin) result.secondNameLatin = secondNameLatin;
	if (latinName) result.latinName = latinName;
	if (birthPlace) result.birthPlace = birthPlace;
	if (issuer) result.issuer = issuer;
	if (registrationAddress) result.registrationAddress = registrationAddress;
	if (birthDate) result.birthDate = birthDate;
	if (issuedAt) result.issuedAt = issuedAt;
	if (validTill) result.validTill = validTill;

	return result;
};

export {formatDateDisplay, extractDateDigits};
